<template>
    <div class="animated fadeIn h-100 admin-container photo-admin-page">
        <div class="page-content">
            <div class="photo-toolbar d-flex flex-wrap align-items-center justify-content-between mb-3">
                <div class="input-group border-bottom position-relative photo-search">
                    <input class="form-control bg-transparent border-none pr-4" :placeholder="$t('common.search')"
                        aria-label="Search" v-model="searchKeyword" />
                    <i class="fa fa-search position-absolute" style="top: 50%; right: 4px; margin-top: -6px" />
                </div>
                <div class="d-flex align-items-center text-nowrap mt-2 mt-md-0">
                    <b-button variant="outline-primary" @click="openCreateModal">
                        <i class="fa fa-plus-circle mr-2"></i>
                        <span class="text-uppercase">Tạo mới</span>
                    </b-button>
                </div>
            </div>

            <b-row v-if="filteredPhotos.length">
                <b-col v-for="item in filteredPhotos" :key="item.id" cols="6" sm="4" md="3" lg="2" xl="2" class="mb-3">
                    <div class="photo-item-card">
                        <div class="photo-preview-wrap" role="button" tabindex="0" @click="openEditModal(item)"
                            @keyup.enter="openEditModal(item)" @keyup.space.prevent="openEditModal(item)">
                            <img v-if="item.images && item.images.length && isImage(item.images[0])"
                                class="photo-preview" :src="mediaUrl(item.images[0])" alt="media preview" />
                            <video v-else-if="item.images && item.images.length" class="photo-preview" controls>
                                <source :src="mediaUrl(item.images[0])" />
                            </video>
                            <div v-else
                                class="photo-preview empty-preview d-flex align-items-center justify-content-center">
                                <span>Chưa có media</span>
                            </div>
                            <span v-if="item.images && item.images.length > 1" class="photo-media-count">
                                +{{ item.images.length - 1 }}
                            </span>
                            <div class="photo-info-overlay">
                                <div class="photo-title">{{ item.description || item.layer?.name || `ID: ${item.id}` }}
                                </div>
                                <div class="photo-coords">{{ item.lat || '-' }}, {{ item.long || '-' }}</div>
                            </div>
                            <div class="photo-actions-overlay">
                                <b-button variant="danger" size="sm" @click.stop="confirmDelete(item)" v-b-tooltip.hover
                                    class="min-width-none" :title="$t('common.delete')">
                                    <i class="fa fa-trash"></i>
                                </b-button>
                            </div>
                        </div>
                    </div>
                </b-col>
            </b-row>

            <b-card v-else class="text-center py-5 photo-empty-state">
                <i class="fa fa-folder-open-o mb-2" style="font-size: 32px"></i>
                <div>Không có dữ liệu phù hợp</div>
            </b-card>

            <b-modal ref="createOrUpdateModal" :title="`${form.id ? $t('common.update') : $t('common.create')}`"
                ok-title="Lưu" @ok="handleCreateOk" :cancel-title="$t('common.cancel')" size="lg">
                <b-form-group label="Vĩ độ">
                    <b-form-input v-model="form.lat" v-validate="'required'" placeholder="Vĩ độ" name="lat"
                        type="number" data-vv-as="Vĩ độ"></b-form-input>
                    <div v-show="errors.has('lat')" class="validation-message text-danger">
                        {{ errors.first('lat') }}
                    </div>
                </b-form-group>
                <b-form-group label="Kinh độ">
                    <b-form-input v-model="form.long" v-validate="'required'" placeholder="Kinh độ" type="number"
                        name="long" data-vv-as="Kinh độ"></b-form-input>
                    <div v-show="errors.has('long')" class="validation-message text-danger">
                        {{ errors.first('long') }}
                    </div>
                </b-form-group>
                <b-form-group label="Mô tả">
                    <b-form-input v-model="form.description" v-validate="'required'" placeholder="Mô tả"
                        name="description" data-vv-as="Mô tả"></b-form-input>
                    <div v-show="errors.has('description')" class="validation-message text-danger">
                        {{ errors.first('description') }}
                    </div>
                </b-form-group>
                <b-form-group label="Liên kết tới lớp dữ liệu">
                    <treeselect :searchable="false" :options="listLayerMap" :normalizer="normalizer"
                        v-model="form.layerId" placeholder="Chọn lớp dữ liệu" />
                </b-form-group>
                <b-form-group label="Chọn hình ảnh, video">
                    <b-form-file v-model="listFile" name="file" multiple placeholder="Chọn hình ảnh, video"
                        accept="video/*,image/*" browse-text="Chọn"></b-form-file>
                    <b-row class="mt-2">
                        <b-col v-for="image in form.images" :key="image" cols="3" class="mb-2">
                            <b-button variant="outline-danger" class="float-right"
                                @click="form.images.splice(form.images.indexOf(image), 1)">
                                <i class="fa fa-trash"></i>
                            </b-button>
                            <b-img v-if="isImage(image)" fluid :src="mediaUrl(image)" />
                            <b-embed v-else type="video" controls aspect="4by3">
                                <source :src="mediaUrl(image)" />
                            </b-embed>
                        </b-col>
                    </b-row>
                </b-form-group>
            </b-modal>
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import API_CONFIG from '@/api-config';
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';

export default {
    components: {
        Treeselect
    },
    async mounted() {
        await this.$nextTick();
        this.listPhoto = await this.getList();
        this.listLayerMap = await this.getLayerTree({ type: 'layer' });
    },
    data: function () {
        return {
            API_CONFIG,
            form: {},
            searchKeyword: null,
            listPhoto: [],
            listFile: [],
            listLayerMap: []
        };
    },
    computed: {
        filteredPhotos() {
            const keyword = (this.searchKeyword || '').toLowerCase().trim();
            if (!keyword) {
                return this.listPhoto;
            }
            return this.listPhoto.filter(item => {
                const content = [
                    item.description,
                    item.layer?.name,
                    item.lat,
                    item.long
                ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase();
                return content.includes(keyword);
            });
        }
    },
    methods: {
        ...mapActions({
            getList: 'photo/getList',
            create: 'photo/create',
            delete: 'photo/delete',
            update: 'photo/update',
            upload: 'file/upload',
            getLayerTree: 'mapLayer/getListTree',
            createAuditByUser: 'user/createAuditByUser'
        }),
        handleCreateOk(modal) {
            modal.preventDefault();
            this.$validator.validateAll().then(result => {
                if (result) {
                    this.submitForm();
                }
            });
        },
        async openEditModal(item) {
            this.form = { ...item, images: [...(item.images || [])] };
            this.listFile = [];
            this.$refs.createOrUpdateModal.show();
        },
        openCreateModal() {
            this.form = { images: [] };
            this.listFile = [];
            this.$refs.createOrUpdateModal.show();
        },
        async confirmDelete(item) {
            const result = await this.$bvModal.msgBoxConfirm('Bạn có chắc chắn muốn xoá không?', {
                title: 'Xác nhận',
                okVariant: 'danger',
                okTitle: 'Có',
                cancelTitle: 'Không'
            });
            if (result) {
                await this.delete(item);
                this.listPhoto = await this.getList();
                this.$toaster.success('Xoá thành công');
                this.createAuditByUser({ content: 'Xoá ảnh' });
            }
        },
        async submitForm() {
            for (const file of this.listFile || []) {
                const data = await this.upload({ formData: { file } });
                this.form.images = this.form.images || [];
                this.form.images.push(data.path);
            }
            if (this.form.id) {
                await this.update({ ...this.form, layerId: this.form.layerId || null });
            } else {
                await this.create(this.form);
            }
            this.$toaster.success('Thành công');
            this.createAuditByUser({ content: 'Cập nhật ảnh' });
            this.listPhoto = await this.getList();
            this.listFile = [];
            this.$refs.createOrUpdateModal.hide();
        },
        normalizer(node) {
            return {
                id: node.id,
                label: node.name,
                children: node.children
            };
        },
        isImage(url) {
            return /\.(jpeg|jpg|gif|png|svg|webp|bmp|ico|tiff|tif|raw|heif|heic)$/i.test(url || '');
        },
        mediaUrl(fileName) {
            return `${API_CONFIG.BASE_URL}download?fileName=${fileName}`;
        }
    }
};
</script>

<style scoped>
.photo-search {
    width: 260px;
    max-width: 100%;
}

.photo-item-card {
    border: 1px solid #deebff;
    border-radius: 10px;
    box-shadow: 0 4px 12px rgba(24, 73, 142, 0.08);
    overflow: hidden;
    background: #f1f7ff;
}

.photo-preview-wrap {
    position: relative;
    height: 230px;
    overflow: hidden;
    background: #f1f7ff;
    cursor: pointer;
}

.photo-preview {
    width: 100%;
    height: 230px;
    object-fit: cover;
    display: block;
}

.empty-preview {
    color: #5577a1;
    font-size: 14px;
}

.photo-media-count {
    position: absolute;
    right: 40px;
    top: 6px;
    padding: 2px 6px;
    border-radius: 999px;
    background: rgba(8, 41, 85, 0.82);
    color: #fff;
    font-size: 10px;
    font-weight: 600;
}

.photo-actions-overlay {
    position: absolute;
    right: 6px;
    top: 6px;
    opacity: 0;
    transition: opacity 0.15s ease-in-out;
}

.photo-preview-wrap:hover .photo-actions-overlay {
    opacity: 1;
}

.photo-info-overlay {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 8px 8px 6px;
    background: linear-gradient(180deg, rgba(4, 18, 40, 0) 0%, rgba(4, 18, 40, 0.85) 100%);
}

.photo-title {
    font-size: 14px;
    color: #fff;
    font-weight: 700;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.photo-coords {
    font-size: 9px;
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.2;
    margin-top: 2px;
}

@media (max-width: 767px) {
    .photo-actions-overlay {
        opacity: 1;
    }
}

.photo-empty-state {
    border: 1px dashed #bed7f6;
    color: #4f6888;
}

@media (max-width: 767px) {
    .photo-toolbar {
        flex-direction: column;
        align-items: flex-start;
    }

    .photo-search {
        width: 100%;
    }
}
</style>
